import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { config } from '@/lib/config';

interface DeleteTopicOptions {
  topicId: string;
  topicTitle?: string;
  onSuccess?: () => void;
}

export const useDeleteTopic = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ topicId }: DeleteTopicOptions) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log(`🗑️ Deleting topic ${topicId} via backend API`);

      // Get auth token for backend API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication token not available');
      }

      // Call backend DELETE endpoint (handles corpus + database cleanup)
      const response = await fetch(`${config.BACKEND_API_URL}/api/topics/${topicId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Topic deletion failed:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Topic deleted successfully:', result);

      return { topicId };
    },
    onSuccess: ({ topicId }, { topicTitle, onSuccess }) => {
      console.log(`✅ Topic ${topicId} deleted successfully`);

      // Invalidate all related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['topics'] });
      queryClient.invalidateQueries({ queryKey: ['topic', topicId] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['topic-sessions', topicId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['topic-documents', topicId] });

      toast({
        title: "Topic deleted",
        description: topicTitle 
          ? `"${topicTitle}" and all related content have been deleted.`
          : "Topic and all related content have been deleted.",
      });

      // Call optional callback
      onSuccess?.();
    },
    onError: (error: Error, { topicTitle }) => {
      console.error('💥 Topic deletion failed:', error);
      
      toast({
        title: "Failed to delete topic",
        description: topicTitle
          ? `Failed to delete "${topicTitle}": ${error.message}`
          : `Failed to delete topic: ${error.message}`,
        variant: "destructive",
      });
    },
    
    // Disable retries to prevent duplicate deletions
    retry: false,
  });

  const deleteTopic = (options: DeleteTopicOptions) => {
    return mutation.mutateAsync(options);
  };

  return {
    deleteTopic,
    isDeleting: mutation.isPending,
    mutation
  };
};

